import {
  RadioGroup,
  useRadio,
  VisuallyHidden,
  cn,
  RadioVariantProps,
  HTMLNextUIProps,
  RadioSlots,
  SlotsToClasses,
  RadioProps
} from '@nextui-org/react'
import { DOMProps, AriaLabelingProps } from '@react-types/shared'
import { Ref, ReactNode } from 'react'

interface Props extends Omit<HTMLNextUIProps<'input'>, keyof RadioVariantProps> {
  ref?: Ref<HTMLElement>
  children?: ReactNode
  description?: string | ReactNode
  classNames?: SlotsToClasses<RadioSlots>
}
export interface AriaRadioProps extends RadioProps, DOMProps, AriaLabelingProps {}
type UseRadioProps = Omit<Props, 'defaultChecked'> &
  Omit<AriaRadioProps, keyof RadioVariantProps> &
  RadioVariantProps

export interface RadioGroupBlockProps {
  fields: {
    options: {
      label: string
      description: string
      value: string
    }[]
    selected: string
  }
}

export function CurieRadio(props: UseRadioProps) {
  const {
    Component,
    children,
    isSelected,
    description,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps
  } = useRadio(props)

  return (
    <Component
      {...getBaseProps()}
      className={cn(
        'group inline-flex items-center hover:opacity-70 active:opacity-50 justify-between flex-row-reverse tap-highlight-transparent',
        'max-w-[300px] cursor-pointer border-1 border-default rounded-lg gap-4 p-2',
        'data-[selected=true]:border-primary'
      )}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <span {...getWrapperProps()}>
        <span {...getControlProps()} />
      </span>
      <div {...getLabelWrapperProps()}>
        {children && <span {...getLabelProps()}>{children}</span>}
        {description && (
          <span className='text-small text-foreground opacity-70'>{description}</span>
        )}
      </div>
    </Component>
  )
}

export function RadioGroupBlock({ fields }: RadioGroupBlockProps) {
  const { options, selected } = fields
  return (
    <RadioGroup defaultValue={selected} className='my-5 ml-[30px]' orientation='horizontal'>
      {options.map(option => {
        return (
          <CurieRadio
            key={option.value}
            description={option.description}
            value={option.value}
            size='sm'
          >
            {option.label}
          </CurieRadio>
        )
      })}
    </RadioGroup>
  )
}
